import { QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

/**
 * A 4xx is a verdict, not a blip — retrying it just delays the error the UI
 * needs to show. Only retry genuine transient failures (5xx / network), and
 * never retry a 401: the api client's refresh-and-retry-once already handled
 * that, so a 401 reaching here means the refresh failed and the session is gone.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status !== undefined && status >= 400 && status < 500) return false;
  }
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // Property/tenant data changes on human timescales; a short window kills
        // the duplicate refetch storm from remounting shells during navigation.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryClient: QueryClient = createQueryClient();
